import type { WritePovTenseChiclet, WriteSettingsChicletValues } from "@sudowrite/shared-chat";
import { resolveChatSpecifiedWordLength } from "~shared/parseExplicitWordCountFromText";

export type { WritePovTenseChiclet, WriteSettingsChicletValues };

export function parseWritePovTenseChiclets(raw: unknown): WritePovTenseChiclet[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const chiclets = raw
    .map((entry) => {
      if (!entry || typeof entry !== "object") return null;
      const label = (entry as { label?: unknown }).label;
      return typeof label === "string" && label.trim() ? { label: label.trim() } : null;
    })
    .filter((entry): entry is WritePovTenseChiclet => entry !== null);
  return chiclets.length > 0 ? chiclets : undefined;
}

export function writePovTenseChicletsWouldRender(chiclets?: WritePovTenseChiclet[] | null): boolean {
  return Array.isArray(chiclets) && chiclets.some((entry) => typeof entry.label === "string" && entry.label.trim());
}

export type AgenticWriteButtonSettingsLike = {
  writeModel?: string;
  wormholeOutputCards?: number;
  wormholeOutputLength?: number;
};

type MessageLikeForWriteGuidance = {
  id?: string;
  role?: string;
  content?: unknown;
  metadata?: { hiddenFromUI?: boolean };
};

/** User text that drives write chiclets — survives streaming before assistant metadata is wired. */
export function resolveUserGuidanceForWriteChiclets(params: {
  messages: MessageLikeForWriteGuidance[];
  triggerUserMessageId?: string | null;
  currentTurnUserMessageId?: string | null;
}): string | undefined {
  const contentFromId = (messageId?: string | null) => {
    if (typeof messageId !== "string" || !messageId.trim()) return undefined;
    const msg = params.messages.find((entry) => entry.id === messageId);
    const content = msg?.content;
    return typeof content === "string" && content.trim() ? content.trim() : undefined;
  };

  const linkedId =
    (typeof params.triggerUserMessageId === "string" && params.triggerUserMessageId.trim())
      ? params.triggerUserMessageId.trim()
      : typeof params.currentTurnUserMessageId === "string" && params.currentTurnUserMessageId.trim()
        ? params.currentTurnUserMessageId.trim()
        : undefined;
  const fromLinked = contentFromId(linkedId);
  if (fromLinked) return fromLinked;

  for (let i = params.messages.length - 1; i >= 0; i -= 1) {
    const entry = params.messages[i];
    if (entry?.role !== "user" || entry.metadata?.hiddenFromUI) continue;
    const content = typeof entry.content === "string" && entry.content.trim() ? entry.content.trim() : undefined;
    if (content) return content;
  }
  return undefined;
}

/** Write-button model for thinking chiclets — persisted tool result wins, then live Write settings. */
export function resolveWriteModelForThinkingChiclet(params: {
  persistedModel?: string | null;
  turnContextSettings?: AgenticWriteButtonSettingsLike | null;
}): string | undefined {
  const persisted =
    typeof params.persistedModel === "string" && params.persistedModel.trim()
      ? params.persistedModel.trim()
      : undefined;
  if (persisted) return persisted;
  const writeModel = params.turnContextSettings?.writeModel;
  if (typeof writeModel === "string" && writeModel.trim()) return writeModel.trim();
  return undefined;
}

/** Show only write constraints established by the agentic turn, never legacy Write-control defaults. */
export function resolveWriteSettingsForThinkingChiclets(params: {
  persisted?: { numOptions?: number; wordLength?: number | null };
  /** Accepted for older turn payloads but intentionally ignored for agentic thinking UI. */
  turnContextSettings?: AgenticWriteButtonSettingsLike | null;
  /** User guidance for the write turn — the source of an explicit chat word count. */
  userGuidance?: string | null;
  /** Accepted for older callers; selection no longer controls legacy-setting visibility. */
  highlightedPassage?: string | null;
}): WriteSettingsChicletValues | undefined {
  const persisted = params.persisted;
  const userGuidance = params.userGuidance ?? "";
  const chatSpecifiedWordLength = resolveChatSpecifiedWordLength(userGuidance);
  const numOptions =
    typeof persisted?.numOptions === "number"
      && Number.isFinite(persisted.numOptions)
      && persisted.numOptions > 1
      ? Math.floor(persisted.numOptions)
      : undefined;
  const wordLength = chatSpecifiedWordLength ?? persisted?.wordLength ?? null;

  if (numOptions == null && wordLength == null) return undefined;
  return {
    ...(numOptions != null ? { numOptions } : {}),
    ...(wordLength != null ? { wordLength } : {}),
  };
}

export function writeSettingsChicletsWouldRender(settings?: WriteSettingsChicletValues | null): boolean {
  if (!settings) return false;
  return (
    (typeof settings.numOptions === "number" && settings.numOptions >= 1)
    || settings.wordLength != null
  );
}

export function parseAgenticWriteButtonSettingsFromTurnContext(
  turnContext: unknown,
): AgenticWriteButtonSettingsLike | null | undefined {
  if (!turnContext || typeof turnContext !== "object") return undefined;
  const raw = (turnContext as { agenticWriteButtonSettings?: unknown }).agenticWriteButtonSettings;
  if (raw === null) return null;
  if (!raw || typeof raw !== "object") return undefined;
  const rec = raw as Record<string, unknown>;
  const wormholeOutputCards =
    typeof rec.wormholeOutputCards === "number" && Number.isFinite(rec.wormholeOutputCards)
      ? rec.wormholeOutputCards
      : undefined;
  const wormholeOutputLength =
    typeof rec.wormholeOutputLength === "number" && Number.isFinite(rec.wormholeOutputLength)
      ? rec.wormholeOutputLength
      : undefined;
  const writeModel = typeof rec.writeModel === "string" && rec.writeModel.trim() ? rec.writeModel.trim() : undefined;
  if (wormholeOutputCards === undefined && wormholeOutputLength === undefined && writeModel === undefined) {
    return undefined;
  }
  return {
    ...(writeModel !== undefined ? { writeModel } : {}),
    ...(wormholeOutputCards !== undefined ? { wormholeOutputCards } : {}),
    ...(wormholeOutputLength !== undefined ? { wormholeOutputLength } : {}),
  };
}
